<template>
  <div class="home-v2">
    <!-- Navigation -->
    <div class="nav-container" :class="{ 'scrolled': isScrolled }">
      <nav class="modern-nav">
        <div class="nav-brand" @click="$router.push('/')">
          <img src="/logo.png" alt="logo" class="brand-logo-img" />
          <span class="brand-name">spaceproxy</span>
        </div>

        <div class="nav-links desktop-only">
          <router-link to="/">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
        </div>

        <div class="nav-actions">
          <a-dropdown :trigger="['click']" placement="bottomCenter">
            <button class="action-icon-btn">
              <a-icon type="global" />
              <span class="lang-text">{{ $i18n.locale === 'zh-CN' ? '中' : 'EN' }}</span>
            </button>
            <a-menu slot="overlay" @click="handleLangClick" class="custom-dark-menu">
              <a-menu-item key="zh-CN">🇨🇳 简体中文</a-menu-item>
              <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
            </a-menu>
          </a-dropdown>

          <template v-if="!isLoggedIn">
            <button class="btn-ghost desktop-only" @click="$router.push('/login')">{{ $t('menu.login') }}</button>
            <button class="btn-primary desktop-only" @click="$router.push('/register')">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <a-dropdown :trigger="['hover']" placement="bottomRight">
              <button class="user-pill desktop-only" @click="$router.push('/dashboard/auth')">
                <div class="u-avatar"><a-icon type="user" /></div>
                <span class="u-name">{{ username }}</span>
              </button>
              <a-menu slot="overlay" class="custom-dark-menu">
                <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                  <a-icon type="appstore" /> {{ $t('menu.dashboard') }}
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout" class="danger-text">
                  <a-icon type="logout" /> {{ $t('menu.logout') }}
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </template>

          <button class="action-icon-btn mobile-only" @click="showMobileMenu = true">
            <a-icon type="menu" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="100%"
      wrapClassName="dark-mobile-drawer"
    >
      <div class="drawer-inner">
        <div class="d-header">
          <div class="nav-brand">
            <img src="/logo.png" alt="logo" class="brand-logo-img" />
            <span class="brand-name">spaceproxy</span>
          </div>
          <button class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </button>
        </div>
        <div class="d-menu">
          <router-link to="/" @click.native="showMobileMenu = false">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center" @click.native="showMobileMenu = false">{{ $t('menu.helpCenter') }}</router-link>
          <a @click="toggleMobileLang"><a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? 'Switch to English' : '切换到中文' }}</a>
        </div>
        <div class="d-footer">
          <template v-if="!isLoggedIn">
            <button class="btn-ghost full-width" @click="$router.push('/login'); showMobileMenu = false">{{ $t('menu.login') }}</button>
            <button class="btn-primary full-width mt-4" @click="$router.push('/register'); showMobileMenu = false">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <div class="d-user">
              <div class="u-avatar"><a-icon type="user" /></div>
              <span>{{ username }}</span>
            </div>
            <button class="btn-ghost full-width mt-4" @click="$router.push('/dashboard/auth'); showMobileMenu = false">{{ $t('menu.dashboard') }}</button>
            <button class="btn-danger full-width mt-4" @click="handleLogout(); showMobileMenu = false">{{ $t('menu.logout') }}</button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg">
        <div class="glow-blob top-right"></div>
        <div class="glow-blob bottom-left"></div>
        <div class="grid-overlay"></div>
      </div>
      
      <div class="hero-container">
        <div class="badge-pill">
          <span class="dot"></span>
          {{ $t('home.hero.badge') }}
        </div>
        
        <h1 class="hero-title">
          <span class="text-gradient">{{ $t('home.hero.title') }}</span>
        </h1>
        
        <p class="hero-desc" v-html="$t('home.hero.subtitle')"></p>
        
        <div class="hero-actions">
          <button class="btn-primary huge" @click="$router.push('/login')">
            {{ $t('home.hero.buyNow') }} <a-icon type="arrow-right" />
          </button>
          <!-- <button class="btn-outline huge" @click="handleContactClick">
            <a-icon type="message" /> {{ $t('home.hero.contactSales') }}
          </button> -->
        </div>

        <div class="stats-bar">
          <div class="stat-block" v-for="(stat, key) in statsData" :key="key">
            <div class="s-val">{{ formatNumber(countUps[key]) }}<span class="s-sfx">{{ stat.suffix }}</span></div>
            <div class="s-lbl">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bento Features Section -->
    <section class="bento-section scroll-reveal">
      <div class="container">
        <div class="section-head">
          <span class="sub-heading">{{ $t('home.features.title') }}</span>
          <h2>{{ $t('home.features.subtitle') }}</h2>
        </div>
        
        <div class="bento-grid">
          <div class="bento-card" v-for="(feature, index) in features" :key="index" :class="`b-card-${index}`">
            <div class="b-icon">
              <a-icon :type="feature.icon" />
            </div>
            <div class="b-content">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </div>
            <div class="b-bg-effect"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Showcase (Dynamic) -->
    <section class="product-showcase scroll-reveal">
      <div class="container">
        <div class="showcase-inner">
          <div class="s-content">
            <h2>{{ $t('home.product.title') }}</h2>
            <p class="s-desc">{{ $t('home.product.desc') }}</p>
            
            <div class="feature-list">
              <div class="f-item" v-for="(item, index) in bannerFeatures" :key="index">
                <div class="f-icon-box"><a-icon :type="item.icon" /></div>
                <div class="f-text">
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.desc }}</p>
                </div>
              </div>
            </div>
            
            <button class="btn-primary" style="margin-top: 48px;" @click="$router.push('/dynamic-residential-proxy')">
              {{ $t('home.product.learnMore') }}
            </button>
          </div>
          
          <div class="s-visual">
            <div class="glass-window">
              <div class="w-header">
                <i></i><i></i><i></i>
              </div>
              <div class="w-body">
                <div class="globe-anim">
                  <a-icon type="global" />
                  <div class="ring r1"></div>
                  <div class="ring r2"></div>
                  <div class="ring r3"></div>
                </div>
                <div class="floating-tag t1"><a-icon type="safety" /> {{ $t('home.banner.safe') }}</div>
                <div class="floating-tag t2"><a-icon type="thunderbolt" /> 99.9% SLA</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Showcase (Static) -->
    <!-- <section class="product-showcase static-showcase scroll-reveal">
      <div class="container">
        <div class="showcase-inner reverse-layout">
          <div class="s-content">
            <h2>{{ $t('home.staticProduct.title') }}</h2>
            <p class="s-desc">{{ $t('home.staticProduct.desc') }}</p>
            
            <div class="feature-list">
              <div class="f-item">
                <div class="f-icon-box accent-box"><a-icon type="lock" /></div>
                <div class="f-text">
                  <h4>{{ $t('home.staticProduct.f1Title') }}</h4>
                  <p>{{ $t('home.staticProduct.f1Desc') }}</p>
                </div>
              </div>
              <div class="f-item">
                <div class="f-icon-box accent-box"><a-icon type="clock-circle" /></div>
                <div class="f-text">
                  <h4>{{ $t('home.staticProduct.f2Title') }}</h4>
                  <p>{{ $t('home.staticProduct.f2Desc') }}</p>
                </div>
              </div>
              <div class="f-item">
                <div class="f-icon-box accent-box"><a-icon type="api" /></div>
                <div class="f-text">
                  <h4>{{ $t('home.staticProduct.f3Title') }}</h4>
                  <p>{{ $t('home.staticProduct.f3Desc') }}</p>
                </div>
              </div>
            </div>
            
            <button class="btn-primary accent-btn" style="margin-top: 48px;" @click="$router.push('/dashboard/auth')">
              {{ $t('home.staticProduct.buyBtn') }}
            </button>
          </div>
          
          <div class="s-visual">
            <div class="glass-window static-window">
              <div class="w-header">
                <i></i><i></i><i></i>
              </div>
              <div class="w-body">
                <div class="shield-anim">
                  <a-icon type="safety-certificate" />
                  <div class="pulse-wave"></div>
                </div>
                <div class="floating-tag t1"><a-icon type="lock" /> {{ $t('home.staticProduct.tag1') }}</div>
                <div class="floating-tag t2"><a-icon type="infinity" /> {{ $t('home.staticProduct.tag2') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section> -->

    <!-- Use Cases Marquee -->
    <section class="use-cases-section scroll-reveal">
      <div class="section-head text-center">
        <h2>{{ $t('home.useCases.title') }}</h2>
        <p>{{ $t('home.useCases.desc') }}</p>
      </div>
      
      <div class="marquee-wrap">
        <div class="marquee-track">
          <div class="uc-pill" v-for="(useCase, index) in useCases" :key="index">
            <a-icon :type="useCase.icon" />
            <span>{{ useCase.name }}</span>
          </div>
          <div class="uc-pill" v-for="(useCase, index) in useCases" :key="'dup-'+index">
            <a-icon :type="useCase.icon" />
            <span>{{ useCase.name }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- How it Works Section -->
    <section class="how-it-works scroll-reveal">
      <div class="container">
        <div class="section-head text-center">
          <span class="sub-heading">{{ $t('home.howItWorks.tag') }}</span>
          <h2>{{ $t('home.howItWorks.title') }}</h2>
          <p>{{ $t('home.howItWorks.desc') }}</p>
        </div>

        <div class="steps-grid">
          <!-- Step 1 -->
          <div class="step-card">
            <div class="step-num">01</div>
            <div class="step-icon">
              <a-icon type="user-add" />
            </div>
            <h3>{{ $t('home.howItWorks.step1Title') }}</h3>
            <p>{{ $t('home.howItWorks.step1Desc') }}</p>
          </div>

          <div class="step-connector desktop-only">
            <div class="line"></div>
            <a-icon type="right" />
          </div>

          <!-- Step 2 -->
          <div class="step-card">
            <div class="step-num">02</div>
            <div class="step-icon">
              <a-icon type="setting" />
            </div>
            <h3>{{ $t('home.howItWorks.step2Title') }}</h3>
            <p>{{ $t('home.howItWorks.step2Desc') }}</p>
          </div>

          <div class="step-connector desktop-only">
            <div class="line"></div>
            <a-icon type="right" />
          </div>

          <!-- Step 3 -->
          <div class="step-card">
            <div class="step-num">03</div>
            <div class="step-icon">
              <a-icon type="rocket" />
            </div>
            <h3>{{ $t('home.howItWorks.step3Title') }}</h3>
            <p>{{ $t('home.howItWorks.step3Desc') }}</p>
          </div>
        </div>
        
        <div class="text-center" style="margin-top: 60px;">
          <button class="btn-primary huge" @click="$router.push('/register')">
            {{ $t('home.howItWorks.btn') }} <a-icon type="arrow-right" />
          </button>
        </div>
      </div>
    </section>

    <!-- Global Map Section -->
    <section class="global-map scroll-reveal">
      <div class="container">
        <div class="section-head text-center">
          <h2>{{ $t('home.map.title') }}</h2>
          <p>{{ $t('home.map.desc') }}</p>
        </div>
        
        <div class="map-grid">
          <div class="country-card" v-for="(country, index) in countries" :key="index">
            <div class="c-head">
              <img :src="`https://flagcdn.com/w40/${country.code}.png`" :alt="country.name" class="c-flag" />
              <span class="c-name">{{ country.name }}</span>
            </div>
            <div class="c-body">
              <div class="c-bar">
                <div class="c-fill" :style="{ width: (Math.random() * 30 + 70) + '%' }"></div>
              </div>
              <span class="c-count">{{ country.count }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Connect Float -->
    <!-- <div class="connect-float">
      <a href="tencent://message/?uin=1337007470" class="c-btn qq-btn">
        <a-icon type="qq" />
        <span class="c-tooltip">QQ</span>
      </a>
      <a href="javascript:void(0)" class="c-btn wechat-btn">
        <a-icon type="wechat" />
        <span class="c-tooltip">Q95082323</span>
      </a>
    </div> -->

    <!-- Footer -->
    <footer class="dark-footer">
      <div class="container">
        <div class="f-grid">
          <div class="f-brand">
            <div class="nav-brand">
              <img src="/logo.png" alt="logo" class="brand-logo-img" />
              <span class="brand-name">spaceproxy</span>
            </div>
            <p>{{ $t('home.footer.desc') }}</p>
          </div>
          <div class="f-links">
            <div class="f-col">
              <h4>{{ $t('home.footer.products') }}</h4>
              <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
              <!-- <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link> -->
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.support') }}</h4>
              <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.contact') }}</h4>
              <div class="contact-item">
                <a-icon type="wechat" />
                <span></span>
              </div>
              <!-- <div class="contact-item">
                <a-icon type="qq" />
                <span>QQ: </span>
              </div> -->
            </div>
          </div>
        </div>
        <div class="f-bottom">
          <p>{{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}</p>
        </div>
      </div>
    </footer>

    <a-back-top :visibilityHeight="300">
      <div class="back-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'Home_view',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false,
      isScrolled: false,
      countUps: {
        ipPool: 0,
        dailyUpdate: 0,
        successRate: 0,
        locations: 0
      }
    };
  },
  computed: {
    statsData() {
      return {
        ipPool: { label: this.$t('home.stats.ipPool'), suffix: '+' },
        dailyUpdate: { label: this.$t('home.stats.dailyUpdate'), suffix: '+' },
        successRate: { label: this.$t('home.stats.successRate'), suffix: '%' },
        locations: { label: this.$t('home.stats.locations'), suffix: '+' }
      };
    },
    bannerFeatures() {
      return [
        { title: this.$t('home.banner.global'), desc: this.$t('home.banner.globalDesc'), icon: 'global' },
        { title: this.$t('home.banner.safe'), desc: this.$t('home.banner.safeDesc'), icon: 'safety-certificate' },
        { title: this.$t('home.banner.exclusive'), desc: this.$t('home.banner.exclusiveDesc'), icon: 'database' },
        { title: this.$t('home.banner.support'), desc: this.$t('home.banner.supportDesc'), icon: 'customer-service' }
      ];
    },
    features() {
      return [
        {
          title: this.$t('home.features.concurrency'),
          description: this.$t('home.features.concurrencyDesc'),
          icon: 'thunderbolt'
        },
        {
          title: this.$t('home.features.resources'),
          description: this.$t('home.features.resourcesDesc'),
          icon: 'global'
        },
        {
          title: this.$t('home.features.diversity'),
          description: this.$t('home.features.diversityDesc'),
          icon: 'appstore'
        },
        {
          title: this.$t('home.features.service'),
          description: this.$t('home.features.serviceDesc'),
          icon: 'customer-service'
        }
      ];
    },
    useCases() {
      return [
        { name: this.$t('home.useCases.dataCollection'), icon: 'database' },
        { name: this.$t('home.useCases.brandProtection'), icon: 'safety-certificate' },
        { name: this.$t('home.useCases.marketing'), icon: 'shop' },
        { name: this.$t('home.useCases.seo'), icon: 'search' },
        { name: this.$t('home.useCases.priceComparison'), icon: 'bar-chart' },
        { name: this.$t('home.useCases.security'), icon: 'lock' },
        { name: this.$t('home.useCases.reviewMonitoring'), icon: 'message' },
        { name: this.$t('home.useCases.adVerification'), icon: 'file-protect' }
      ];
    },
    countries() {
      return [
        { name: this.$t('countries.us'), count: '11M+', code: 'us' },
        { name: this.$t('countries.br'), count: '9.9M+', code: 'br' },
        { name: this.$t('countries.de'), count: '5.9M+', code: 'de' },
        { name: this.$t('countries.fr'), count: '8.2M+', code: 'fr' },
        { name: this.$t('countries.in'), count: '4.3M+', code: 'in' },
        { name: this.$t('countries.ca'), count: '14M+', code: 'ca' },
        { name: this.$t('countries.gb'), count: '7.2M+', code: 'gb' },
        { name: this.$t('countries.es'), count: '16M+', code: 'es' },
      ];
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.animateNumbers();
    this.initScrollReveal();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    toggleMobileLang() {
      const newLang = this.$i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$i18n.locale = newLang;
      localStorage.setItem('language', newLang);
    },
    handleContactClick() {
      this.$message.info('请联系');
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    formatNumber(num) {
      if (typeof num === 'number') {
        return num.toLocaleString();
      }
      return num;
    },
    animateNumbers() {
      const targets = {
        ipPool: 98560124,
        dailyUpdate: 5214890,
        successRate: 99.8,
        locations: 220
      };
      
      const duration = 2000;
      const frameDuration = 1000 / 60;
      const totalFrames = Math.round(duration / frameDuration);
      
      let frame = 0;
      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const easeOutQuad = t => t * (2 - t);
        
        if (frame <= totalFrames) {
          this.countUps.ipPool = Math.round(targets.ipPool * easeOutQuad(progress));
          this.countUps.dailyUpdate = Math.round(targets.dailyUpdate * easeOutQuad(progress));
          this.countUps.successRate = Number((targets.successRate * easeOutQuad(progress)).toFixed(1));
          this.countUps.locations = Math.round(targets.locations * easeOutQuad(progress));
        } else {
          this.countUps = targets;
          clearInterval(timer);
        }
      }, frameDuration);
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const elements = document.querySelectorAll('.scroll-reveal');
        elements.forEach(el => {
          el.style.opacity = '0';
          el.style.transform = 'translateY(40px)';
          el.style.transition = 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)';
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style lang="less">
/* Global Unscoped Overrides for Dropdowns & Drawer */
.custom-dark-menu {
  background: #18181b !important;
  border: 1px solid #27272a !important;
  border-radius: 12px !important;
  padding: 8px !important;
  box-shadow: 0 10px 40px rgba(0,0,0,0.5) !important;

  .ant-dropdown-menu-item {
    color: #a1a1aa !important;
    border-radius: 8px !important;
    padding: 10px 16px !important;
    transition: all 0.2s !important;
    
    &:hover {
      background: #27272a !important;
      color: #f4f4f5 !important;
    }
    
    .anticon {
      color: #8b5cf6 !important;
      margin-right: 10px;
    }
  }
  
  .ant-dropdown-menu-item-divider {
    background-color: #27272a !important;
    margin: 8px 0 !important;
  }
  
  .danger-text {
    &:hover {
      background: rgba(239, 68, 68, 0.1) !important;
      color: #ef4444 !important;
      .anticon { color: #ef4444 !important; }
    }
  }
}

.dark-mobile-drawer {
  .ant-drawer-content {
    background: #09090b;
    color: #f4f4f5;
  }
  .ant-drawer-body {
    padding: 0;
    height: 100%;
  }
  .drawer-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;
    
    .d-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 40px;
      
      .close-btn {
        background: #18181b;
        border: 1px solid #27272a;
        color: #f4f4f5;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
    }
    
    .d-menu {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      a {
        color: #a1a1aa;
        font-size: 20px;
        font-weight: 600;
        padding: 12px 0;
        border-bottom: 1px solid #18181b;
        transition: color 0.3s;
        
        &:hover, &.router-link-exact-active {
          color: #f4f4f5;
        }
      }
    }
    
    .d-footer {
      padding-top: 24px;
      border-top: 1px solid #27272a;
      
      .d-user {
        display: flex;
        align-items: center;
        gap: 12px;
        color: #f4f4f5;
        font-weight: 600;
        margin-bottom: 20px;
        
        .u-avatar {
          width: 40px;
          height: 40px;
          background: #8b5cf6;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
      
      .full-width {
        width: 100%;
        height: 50px;
        border-radius: 12px;
        font-weight: 600;
        font-size: 16px;
      }
      
      .mt-4 { margin-top: 16px; }
      
      .btn-danger {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
        border: 1px solid rgba(239, 68, 68, 0.2);
        cursor: pointer;
      }
    }
  }
}
</style>

<style scoped lang="less">
/* Variables */
@bg-dark: #09090b;
@card-bg: #18181b;
@border: #27272a;
@primary: #8b5cf6;
@primary-hover: #a855f7;
@accent: #3b82f6;
@text-main: #f4f4f5;
@text-muted: #a1a1aa;

/* Base Reset for component */
.home-v2 {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background-color: @bg-dark;
  color: @text-main;
  min-height: 100vh;
  overflow-x: hidden;
}

button {
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Animations */
.scroll-reveal.is-visible {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Navigation */
.nav-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px;
  transition: all 0.4s ease;
  
  &.scrolled {
    padding: 10px 20px;
    
    .modern-nav {
      background: rgba(24, 24, 27, 0.8);
      backdrop-filter: blur(12px);
      border-color: @border;
      box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    }
  }
}

.modern-nav {
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  transition: all 0.4s ease;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  .brand-logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
  }
  
  .brand-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, @primary, @accent);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    
    svg { width: 20px; height: 20px; }
  }
  
  .brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: @text-main;
  }
}

.nav-links {
  display: flex;
  gap: 32px;
  
  a {
    color: @text-muted;
    font-weight: 500;
    font-size: 15px;
    transition: color 0.3s;
    
    &:hover, &.router-link-exact-active {
      color: @text-main;
    }
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-icon-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: @text-muted;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s;
  
  &:hover {
    background: @card-bg;
    color: @text-main;
  }
}

.btn-ghost {
  color: @text-main;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s;
  
  &:hover { background: @card-bg; }
}

.btn-primary {
  background: linear-gradient(135deg, @primary, @accent);
  color: #fff;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
  
  &.huge {
    padding: 16px 36px;
    font-size: 18px;
    border-radius: 14px;
  }
}

.btn-outline {
  border: 1px solid @border;
  background: @card-bg;
  color: @text-main;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  
  &:hover {
    border-color: @text-muted;
    background: rgba(255,255,255,0.05);
  }
  
  &.huge {
    padding: 16px 36px;
    font-size: 18px;
    border-radius: 14px;
  }
}

.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: @card-bg;
  border: 1px solid @border;
  padding: 4px 16px 4px 4px;
  border-radius: 20px;
  color: @text-main;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  
  .u-avatar {
    width: 28px;
    height: 28px;
    background: @primary;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  
  &:hover {
    border-color: @primary;
  }
}

.mobile-only { display: none; }

/* Hero Section */
.hero {
  position: relative;
  padding: 180px 0 100px;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.hero-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  overflow: hidden;
  z-index: 0;
  
  .glow-blob {
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.15;
    animation: float 10s infinite alternate;
    
    &.top-right {
      background: @primary;
      top: -200px;
      right: -100px;
    }
    
    &.bottom-left {
      background: @accent;
      bottom: -200px;
      left: -100px;
      animation-delay: -5s;
    }
  }
  
  .grid-overlay {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 80%);
  }
}

@keyframes float {
  0% { transform: translate(0, 0); }
  100% { transform: translate(30px, 30px); }
}

.hero-container {
  position: relative;
  z-index: 1;
  max-width: 1400px; /* 进一步放宽容器，给大数字足够空间 */
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  color: @primary;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 32px;
  
  .dot {
    width: 8px;
    height: 8px;
    background: @primary;
    border-radius: 50%;
    box-shadow: 0 0 10px @primary;
  }
}

.hero-title {
  font-size: 72px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -2px;
  margin-bottom: 24px;
  
  .text-gradient {
    background: linear-gradient(to right, #fff, #a1a1aa);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.hero-desc {
  font-size: 20px;
  color: @text-muted;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto 48px;
}

.hero-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 80px;
}

.stats-bar {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 40px;
  padding-top: 60px;
  border-top: 1px solid rgba(255,255,255,0.05);
  width: 100%;
  
  .stat-block {
    flex: 0 1 auto;
    min-width: 200px;
    text-align: center;
    
    .s-val {
      font-size: 36px; /* 进一步缩小一点字体，避免长数字溢出 */
      font-weight: 800;
      color: #fff;
      line-height: 1.2;
      margin-bottom: 8px;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      
      .s-sfx {
        font-size: 20px;
        color: @primary;
        margin-left: 2px;
      }
    }
    
    .s-lbl {
      color: @text-muted;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 600;
    }
  }
}

/* Sections Global */
.section-head {
  margin-bottom: 60px;
  
  .sub-heading {
    color: @primary;
    font-weight: 600;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: block;
    margin-bottom: 12px;
  }
  
  h2 {
    font-size: 40px;
    font-weight: 800;
    color: #fff;
    margin: 0;
    letter-spacing: -1px;
  }
  
  p {
    color: @text-muted;
    font-size: 18px;
    margin-top: 16px;
  }
  
  &.text-center { text-align: center; }
}

/* Bento Features */
.bento-section { padding: 100px 0; }

.bento-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 240px;
  gap: 24px;
  
  .bento-card {
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 24px;
    padding: 32px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.4s;
    
    &:hover {
      border-color: rgba(139, 92, 246, 0.4);
      transform: translateY(-5px);
      
      .b-bg-effect { opacity: 1; }
      .b-icon { background: @primary; color: #fff; }
    }
    
    .b-icon {
      width: 48px;
      height: 48px;
      background: rgba(255,255,255,0.05);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: @text-main;
      margin-bottom: auto;
      transition: all 0.4s;
      z-index: 1;
    }
    
    .b-content {
      z-index: 1;
      h3 { font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 8px; }
      p { color: @text-muted; font-size: 15px; margin: 0; line-height: 1.5; }
    }
    
    .b-bg-effect {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: radial-gradient(circle at top right, rgba(139, 92, 246, 0.1), transparent 60%);
      opacity: 0;
      transition: opacity 0.4s;
      z-index: 0;
    }
  }
  
  .b-card-0 { grid-column: span 2; }
  .b-card-1 { grid-column: span 1; }
  .b-card-2 { grid-column: span 1; }
  .b-card-3 { grid-column: span 2; }
}

/* Product Showcase */
.product-showcase { padding: 100px 0; background: #000; border-top: 1px solid @border; border-bottom: 1px solid @border; }

.static-showcase {
  background: @bg-dark;
  border-top: none;
  .showcase-inner.reverse-layout {
    flex-direction: row-reverse;
  }
  .accent-box {
    background: rgba(59, 130, 246, 0.1) !important;
    color: @accent !important;
  }
  .accent-btn {
    background: linear-gradient(135deg, @accent, #0ea5e9);
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
    &:hover {
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    }
  }
  .static-window .w-body {
    background: radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    .shield-anim {
      font-size: 80px;
      color: @accent;
      position: relative;
      .pulse-wave {
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 120px; height: 120px;
        border-radius: 50%;
        background: rgba(59, 130, 246, 0.2);
        animation: pulse-shield 2s infinite ease-in-out;
      }
    }
    .floating-tag i { color: @accent; }
  }
}

@keyframes pulse-shield {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
}

.showcase-inner {
  display: flex;
  align-items: center;
  gap: 80px;
}

.s-content {
  flex: 1;
  h2 { font-size: 48px; font-weight: 800; color: #fff; margin-bottom: 24px; line-height: 1.1; }
  .s-desc { font-size: 18px; color: @text-muted; margin-bottom: 40px; line-height: 1.6; }
  
  .feature-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    
    .f-item {
      display: flex;
      gap: 16px;
      
      .f-icon-box {
        width: 48px; height: 48px;
        background: rgba(139, 92, 246, 0.1);
        color: @primary;
        border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        font-size: 24px;
        flex-shrink: 0;
      }
      
      .f-text {
        h4 { font-size: 18px; font-weight: 700; color: #fff; margin-bottom: 4px; }
        p { color: @text-muted; font-size: 15px; margin: 0; }
      }
    }
  }
}

.s-visual {
  flex: 1;
  
  .glass-window {
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
    
    .w-header {
      background: rgba(255,255,255,0.02);
      padding: 12px 16px;
      border-bottom: 1px solid @border;
      display: flex; gap: 8px;
      
      i { width: 12px; height: 12px; border-radius: 50%; background: #3f3f46; }
      i:nth-child(1) { background: #ef4444; }
      i:nth-child(2) { background: #eab308; }
      i:nth-child(3) { background: #22c55e; }
    }
    
    .w-body {
      height: 400px;
      position: relative;
      display: flex; align-items: center; justify-content: center;
      background: radial-gradient(circle at center, rgba(139, 92, 246, 0.1) 0%, transparent 70%);
      
      .globe-anim {
        font-size: 64px; color: @primary; position: relative;
        
        .ring {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 50%;
          animation: pulse-ring 4s infinite linear;
        }
        .r1 { width: 100px; height: 100px; }
        .r2 { width: 150px; height: 150px; animation-delay: -1.3s; }
        .r3 { width: 200px; height: 200px; animation-delay: -2.6s; }
      }
      
      .floating-tag {
        position: absolute;
        background: rgba(24, 24, 27, 0.8);
        backdrop-filter: blur(8px);
        border: 1px solid @border;
        padding: 10px 20px;
        border-radius: 12px;
        font-weight: 600; color: #fff;
        display: flex; align-items: center; gap: 8px;
        animation: float 6s infinite ease-in-out;
        
        i { color: @accent; }
        
        &.t1 { top: 40px; left: 40px; }
        &.t2 { bottom: 40px; right: 40px; animation-delay: -3s; }
      }
    }
  }
}

@keyframes pulse-ring {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
}

/* Marquee Use Cases */
.use-cases-section { padding: 100px 0; overflow: hidden; }

.marquee-wrap {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 20px 0;
  
  &::before, &::after {
    content: ''; position: absolute; top: 0; width: 200px; height: 100%; z-index: 2;
  }
  &::before { left: 0; background: linear-gradient(to right, @bg-dark, transparent); }
  &::after { right: 0; background: linear-gradient(to left, @bg-dark, transparent); }
}

.marquee-track {
  display: inline-flex;
  animation: marquee 40s linear infinite;
  
  .uc-pill {
    display: inline-flex; align-items: center; gap: 12px;
    padding: 16px 32px;
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 100px;
    margin: 0 12px;
    font-size: 18px; font-weight: 600; color: @text-main;
    white-space: nowrap;
    transition: all 0.3s;
    
    i { color: @primary; font-size: 24px; }
    
    &:hover {
      background: rgba(139, 92, 246, 0.1);
      border-color: @primary;
      transform: scale(1.05);
    }
  }
}

@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* How it Works */
.how-it-works {
  padding: 100px 0;
  background: #000;
  border-top: 1px solid @border;
}

.text-center { text-align: center; }

.steps-grid {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
  
  .step-card {
    flex: 1;
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 20px;
    padding: 40px 30px;
    position: relative;
    text-align: center;
    transition: all 0.4s;
    z-index: 2;
    
    &:hover {
      border-color: @primary;
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(139, 92, 246, 0.1);
      
      .step-icon {
        background: @primary;
        color: #fff;
      }
    }
    
    .step-num {
      position: absolute;
      top: 20px;
      right: 20px;
      font-size: 48px;
      font-weight: 900;
      color: rgba(255, 255, 255, 0.05);
      line-height: 1;
    }
    
    .step-icon {
      width: 64px;
      height: 64px;
      background: rgba(139, 92, 246, 0.1);
      color: @primary;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      margin: 0 auto 24px;
      transition: all 0.4s;
    }
    
    h3 {
      font-size: 20px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 16px;
    }
    
    p {
      color: @text-muted;
      font-size: 15px;
      line-height: 1.6;
      margin: 0;
    }
  }
  
  .step-connector {
    flex-shrink: 0;
    width: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-top: 100px;
    color: @text-muted;
    font-size: 20px;
    
    .line {
      position: absolute;
      width: 100%;
      height: 2px;
      background: dashed 2px @border;
      z-index: 1;
    }
  }
}

/* Global Map */
.global-map { padding: 100px 0; }

.map-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  
  .country-card {
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 16px;
    padding: 20px;
    transition: all 0.3s;
    
    &:hover {
      background: rgba(255,255,255,0.02);
      border-color: rgba(255,255,255,0.1);
      transform: translateY(-2px);
    }
    
    .c-head {
      display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
      .c-flag { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
      .c-name { font-weight: 600; color: #fff; font-size: 16px; }
    }
    
    .c-body {
      .c-bar {
        height: 6px; background: #27272a; border-radius: 3px; overflow: hidden; margin-bottom: 8px;
        .c-fill { height: 100%; background: linear-gradient(90deg, @primary, @accent); border-radius: 3px; }
      }
      .c-count { font-size: 14px; color: @text-muted; }
    }
  }
}

/* Floating Connect */
.connect-float {
  position: fixed; right: 24px; bottom: 100px; z-index: 99;
  display: flex; flex-direction: column; gap: 12px;
  
  .c-btn {
    width: 50px; height: 50px;
    background: @card-bg; border: 1px solid @border; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: @text-main; font-size: 20px;
    position: relative; transition: all 0.3s;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    
    &.telegram-btn:hover { border-color: #0088cc; color: #0088cc; transform: scale(1.1); }
    &.qq-btn:hover { border-color: #12b7f5; color: #12b7f5; transform: scale(1.1); }
    &.wechat-btn:hover { border-color: #07c160; color: #07c160; transform: scale(1.1); }
    
    &:hover {
      .c-tooltip { opacity: 1; visibility: visible; transform: translateX(0); }
    }
    
    .c-tooltip {
      position: absolute; right: 60px;
      background: @text-main; color: @bg-dark;
      padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600;
      white-space: nowrap;
      opacity: 0; visibility: hidden; transform: translateX(10px); transition: all 0.3s;
    }
  }
}

/* Back Top */
.back-top-btn {
  width: 50px; height: 50px;
  background: @card-bg; border: 1px solid @border; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: @text-main; font-size: 20px;
  transition: all 0.3s;
  
  &:hover { background: @primary; border-color: @primary; color: #fff; }
}

/* Footer */
.dark-footer {
  background: #000; border-top: 1px solid @border; padding: 80px 0 40px;
  
  .f-grid {
    display: flex; justify-content: space-between; gap: 40px; margin-bottom: 60px;
  }
  
  .f-brand {
    max-width: 300px;
    p { color: @text-muted; line-height: 1.6; margin-top: 20px; }
  }
  
  .f-links {
    display: flex; gap: 80px;
    
    .f-col {
      display: flex; flex-direction: column; gap: 16px;
      h4 { color: #fff; font-weight: 700; font-size: 16px; margin: 0; }
      a { color: @text-muted; transition: color 0.3s; &:hover { color: @primary; } }
      .contact-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: @text-muted;
        font-size: 14px;
        i { color: @primary; font-size: 16px; }
      }
    }
  }
  
  .f-bottom {
    border-top: 1px solid @border; padding-top: 30px; text-align: center;
    p { color: @text-muted; font-size: 14px; margin: 0; }
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-title { font-size: 56px; }
  .stats-bar { flex-wrap: wrap; gap: 40px; justify-content: center; }
  .stats-bar .stat-block { flex: 0 0 40%; min-width: auto; } /* 平板下变成两行两列 */
  .bento-grid {
    grid-template-columns: 1fr 1fr;
    .b-card-0, .b-card-3 { grid-column: span 2; }
    .b-card-1, .b-card-2 { grid-column: span 1; }
  }
  .showcase-inner { flex-direction: column; }
  .map-grid { grid-template-columns: repeat(2, 1fr); }
  .f-grid { flex-direction: column; }
  .f-links { flex-wrap: wrap; gap: 40px; }
}

@media (max-width: 768px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
  
  .nav-container { padding: 10px; }
  .modern-nav { height: 56px; padding: 0 16px; border-radius: 12px; background: rgba(24, 24, 27, 0.9); }
  
  .hero { padding: 120px 0 60px; }
  .hero-title { font-size: 40px; }
  .hero-desc { font-size: 16px; }
  .hero-actions { flex-direction: column; gap: 16px; button { width: 100%; } }
  
  .stats-bar { gap: 24px; flex-direction: column; }
  .stats-bar .stat-block { width: 100%; }
  
  .bento-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    .b-card-0, .b-card-1, .b-card-2, .b-card-3 { grid-column: span 1; }
  }
  
  .product-showcase { padding: 60px 0; }
  .showcase-inner { flex-direction: column !important; gap: 40px; }
  .s-content { 
    text-align: center;
    h2 { font-size: 32px; }
    .feature-list {
      .f-item {
        flex-direction: column;
        align-items: center;
        text-align: center;
        .f-icon-box { margin-bottom: 8px; }
      }
    }
    .btn-primary { width: 100%; }
  }
  .s-visual { 
    width: 100%;
    .glass-window .w-body { 
      height: 300px;
      .globe-anim { font-size: 48px; }
      .floating-tag {
        font-size: 12px;
        padding: 8px 12px;
        &.t1 { top: 20px; left: 20px; }
        &.t2 { bottom: 20px; right: 20px; }
      }
    }
  }
  
  .map-grid { grid-template-columns: 1fr; }
  .f-links { gap: 30px; flex-direction: column; }
  
  .steps-grid { flex-direction: column; gap: 24px; }
  .step-connector { display: none !important; }
}
</style>